import posthog from 'posthog-js';
import { isDesktop, isMobile } from '@/lib/platform';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

export type EntityKind =
  | 'document'
  | 'canvas'
  | 'collection'
  | 'recording'
  | 'space'
  | 'conversation';

export type AnalyticsEvent =
  | { name: 'entity_created'; props: { entity_kind: EntityKind; source?: string } }
  | {
      name: 'recording_started';
      props: { source: string; has_calendar_event: boolean };
    }
  | { name: 'recording_start_blocked'; props: { reason: string } }
  | {
      name: 'ai_edit_resolved';
      props: { disposition: string; action: string; entity_kind: string; mode: string };
    }
  // Tier 2 — adoption and monetization.
  // search_performed deliberately carries query_length, never the query text:
  // note titles and bodies are private content and must not reach analytics.
  | {
      name: 'search_performed';
      props: { query_length: number; result_count: number; has_results: boolean };
    }
  | { name: 'search_result_selected'; props: { rank: number; entity_kind: string; result_count: number } }
  | { name: 'paywall_shown'; props: { blocking_action: string; credit_state?: string } }
  | { name: 'meeting_detected_resolved'; props: { action: string; source: string } }
  | { name: 'calendar_connect_started'; props: { provider: string; source: string } }
  | { name: 'invitation_sent'; props: { permission: string; resource_type: string } }
  | { name: 'invitation_accepted'; props: { resource_type: string } }
  | { name: 'assistant_message_sent'; props: { surface: string; context_doc_count: number } }
  // Collaboration and permissions. Both deliberately omit the entity id: these
  // fire on shared documents, and an id is a handle to someone else's content.
  | { name: 'entity_deleted_remote'; props: { entity_kind: EntityKind } }
  | { name: 'entity_access_denied'; props: { reason: string } }
  // Assistant run outcomes. Conversation ids are deliberately absent: these
  // answer "how often do runs finish or stall on approval", not "which run".
  | { name: 'assistant_run_finished'; props: { had_approval: boolean } }
  | { name: 'assistant_run_needs_approval'; props: Record<string, never> }
  // Workspace navigation. tab_type separates opening a document from a named
  // view (home, trash); entity ids stay out, as everywhere else.
  | { name: 'tab_opened'; props: { tab_type: string } }
  | { name: 'tab_closed'; props: Record<string, never> };

type EventName = AnalyticsEvent['name'];
type PropsFor<N extends EventName> = Extract<AnalyticsEvent, { name: N }>['props'];

function platform(): string {
  if (isDesktop) return 'desktop';
  if (isMobile) return 'mobile';
  return 'web';
}

export type AnalyticsObserver = <N extends EventName>(name: N, props: PropsFor<N>) => void;

const observers = new Set<AnalyticsObserver>();

/**
 * Watch the product-analytics stream.
 *
 * These events already mark the moments the product considers meaningful, and
 * they are placed at the call sites that own them — so anything that wants to
 * react to "something happened" can read them instead of growing its own
 * producer beside each one. Observers see the event even when the analytics
 * backend is unavailable.
 *
 * Observers must not throw; one that does is isolated so it cannot break the
 * caller or its siblings. They also must not assume every product event is
 * published here — a call site added without `capture` is invisible.
 */
export function onAnalyticsEvent(observer: AnalyticsObserver): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

/** Test-only: the observer set is module state and outlives a render. */
export function resetAnalyticsObserversForTests(): void {
  observers.clear();
}

/**
 * Emit a product analytics event with the global properties every event carries.
 *
 * Analytics must never break a user flow, so a failure here is swallowed rather
 * than propagated to the calling feature code.
 */
export function capture<N extends EventName>(name: N, props: PropsFor<N>): void {
  // Before the network call, and individually guarded: an observer must see
  // the event whether or not telemetry reaches PostHog.
  for (const observer of observers) {
    try {
      observer(name, props);
    } catch {
      // An observer is a reaction, never a reason to fail the caller.
    }
  }

  try {
    const workspace = useWorkspaceStore.getState().getActiveWorkspace();
    posthog.capture(name, {
      ...props,
      platform: platform(),
      ...(workspace ? { workspace_id: workspace.id, workspace_type: workspace.type } : {}),
    });
  } catch {
    // Intentionally ignored: telemetry must not surface to the user.
  }
}
